import { createAiUiLogger, previewText } from "./log";
import type { ClientContext } from "@maintainer-pro/ai-cli";

export type MaintainerProClientOptions = {
  baseUrl: string;
  apiKey: string;
  fetchImpl?: typeof fetch;
};

export type MaintainerProApplication = {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  /** @deprecated Use clientId */
  companyId?: string;
  /** @deprecated Use clientName */
  companyName?: string;
  partnerId: string;
  sandboxId?: string;
  sandboxName?: string;
};

export type MaintainerProStoredMessage = {
  id: string;
  conversationId: string;
  role: string;
  content: string;
  provider?: string | null;
  senderType?: string | null;
  senderName?: string | null;
  parentMessageId?: string | null;
  queueStatus?: string | null;
  queuePosition?: number | null;
  createdAt?: string;
  attachmentPaths?: string[];
  attachmentIds?: string[];
  attachments?: Array<{
    id: string;
    mimeType: string;
    originalName: string;
  }>;
};

export type MaintainerProConversationSummary = {
  id: string;
  updatedAt: string;
  messages: Array<{
    role: string;
    content: string;
    createdAt?: string;
  }>;
};

export type MaintainerProChatUser = {
  name: string;
  type: "client" | "developer" | "ai";
  default?: boolean;
  email?: string;
  role?: string;
};

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

export function createMaintainerProClient(options: MaintainerProClientOptions) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const log = createAiUiLogger("mp");

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const method = init?.method ?? "GET";
    log.debug({ method, path }, "request");
    const started = Date.now();
    const res = await fetchImpl(joinUrl(options.baseUrl, path), {
      ...init,
      headers: {
        Authorization: `Bearer ${options.apiKey}`,
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      log.warn(
        { method, path, status: res.status, ms: Date.now() - started },
        "request failed"
      );
      throw new Error(
        `Maintainer Pro ${path} failed (${res.status}): ${text || res.statusText}`
      );
    }
    log.debug(
      { method, path, status: res.status, ms: Date.now() - started },
      "request ok"
    );
    return (await res.json()) as T;
  }

  return {
    async getApplication() {
      const data = await request<{ application: MaintainerProApplication }>(
        "/api/v1/client/application"
      );
      return data.application;
    },

    async listConversations() {
      const data = await request<{
        conversations: MaintainerProConversationSummary[];
      }>("/api/v1/store/conversations");
      return data.conversations;
    },

    async listChatUsers() {
      const data = await request<{ users: MaintainerProChatUser[] }>(
        "/api/v1/client/chat-users"
      );
      return data.users ?? [];
    },

    /** Cookie session on this origin: client users, or partner staff as developer. */
    async chatSession() {
      const headers = {
        Authorization: `Bearer ${options.apiKey}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      const urls: string[] = [];
      if (typeof window !== "undefined") {
        urls.push(`${window.location.origin}/api/v1/client/chat-session`);
      }
      const configured = joinUrl(options.baseUrl, "/api/v1/client/chat-session");
      if (!urls.includes(configured)) urls.push(configured);

      for (const url of urls) {
        try {
          const res = await fetchImpl(url, {
            credentials: "include",
            headers,
          });
          if (!res.ok) continue;
          const data = (await res.json()) as {
            user: {
              name: string;
              email: string;
              type: "client" | "developer";
              role?: string;
            } | null;
          };
          const user = data.user;
          if (
            !user?.email ||
            (user.type !== "client" && user.type !== "developer")
          ) {
            continue;
          }
          return user;
        } catch {
          /* try next origin */
        }
      }
      return null;
    },

    async authProviders() {
      const data = await request<{ google: boolean }>(
        "/api/v1/client/auth/providers"
      );
      return data;
    },

    async startChatGoogle(returnOrigin: string) {
      const data = await request<{ url: string }>(
        "/api/v1/client/auth/google/start",
        {
          method: "POST",
          body: JSON.stringify({ returnOrigin }),
        }
      );
      const path = data.url.startsWith("http")
        ? data.url
        : joinUrl(options.baseUrl, data.url);
      return path;
    },

    async loginChatUser(email: string, password: string) {
      const res = await fetchImpl(
        joinUrl(options.baseUrl, "/api/v1/client/chat-login"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${options.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );
      if (!res.ok) {
        if (res.status === 401) {
          const body = (await res.json().catch(() => null)) as {
            error?: string;
          } | null;
          throw new Error(body?.error || "Invalid email or password");
        }
        if (res.status === 403) {
          throw new Error("Account disabled");
        }
        const text = await res.text().catch(() => "");
        throw new Error(text || `Sign in failed (${res.status})`);
      }
      const data = (await res.json()) as {
        user: {
          name: string;
          email: string;
          type: "client" | "developer";
          role?: string;
        };
      };
      return data.user;
    },

    async uploadAttachment(
      conversationId: string,
      item: { name: string; mimeType: string; data: string }
    ) {
      const data = await request<{
        attachment: { id: string; ref?: string };
      }>("/api/v1/store/attachments", {
        method: "POST",
        body: JSON.stringify({
          conversationId,
          name: item.name,
          mimeType: item.mimeType,
          data: item.data,
        }),
      });
      return data.attachment;
    },

    async sendUserMessage(input: {
      conversationId: string;
      id?: string;
      content: string;
      senderType?: "client" | "developer";
      senderName?: string;
      attachmentIds?: string[];
      parentMessageId?: string;
      /** Ignored — admin server owns queue assignment. */
      queueStatus?: "working" | "queued";
      context?: ClientContext;
    }) {
      log.debug(
        {
          conversationId: input.conversationId,
          attachments: input.attachmentIds?.length ?? 0,
          preview: previewText(input.content),
        },
        "sendUserMessage"
      );
      const data = await request<{
        message: MaintainerProStoredMessage;
      }>(
        `/api/v1/store/conversations/${encodeURIComponent(input.conversationId)}/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            id: input.id,
            role: "user",
            content: input.content,
            senderType: input.senderType ?? "client",
            senderName: input.senderName,
            attachmentIds: input.attachmentIds,
            parentMessageId: input.parentMessageId,
            intent: "queue",
            context: input.context,
          }),
        }
      );
      return data.message;
    },

    async sendDeveloperMessage(input: {
      conversationId: string;
      id?: string;
      content: string;
      senderName?: string;
      parentMessageId?: string;
    }) {
      const data = await request<{
        message: MaintainerProStoredMessage;
      }>(
        `/api/v1/store/conversations/${encodeURIComponent(input.conversationId)}/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            id: input.id,
            role: "assistant",
            content: input.content,
            senderType: "developer",
            senderName: input.senderName,
            parentMessageId: input.parentMessageId,
            provider: "developer",
          }),
        }
      );
      return data.message;
    },

    async updateQueueStatus(input: {
      conversationId: string;
      messageId: string;
      queueStatus: "working" | "queued" | null;
    }) {
      const data = await request<{
        message: MaintainerProStoredMessage;
      }>(
        `/api/v1/store/conversations/${encodeURIComponent(input.conversationId)}/messages/${encodeURIComponent(input.messageId)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ queueStatus: input.queueStatus }),
        }
      );
      return data.message;
    },

    async setWorkingPlaceholder(input: {
      conversationId: string;
      parentMessageId: string;
    }) {
      const data = await request<{
        message: MaintainerProStoredMessage | null;
      }>(
        `/api/v1/store/conversations/${encodeURIComponent(input.conversationId)}/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            role: "assistant",
            content: "",
            provider: "working",
            senderType: "ai",
            parentMessageId: input.parentMessageId,
          }),
        }
      );
      return data.message;
    },

    async recoverQueue(conversationId: string) {
      return request<{
        ok: boolean;
        action: string;
        workingId?: string | null;
        promotedId?: string | null;
        nextQueued?: { id: string; content: string } | null;
      }>(
        `/api/v1/store/conversations/${encodeURIComponent(conversationId)}/recover-queue`,
        { method: "POST", body: "{}" }
      );
    },

    async listMessages(conversationId: string) {
      const data = await request<{
        conversationId: string;
        messages: MaintainerProStoredMessage[];
      }>(
        `/api/v1/store/conversations/${encodeURIComponent(conversationId)}/messages`
      );
      return data.messages ?? [];
    },

    attachmentUrl(attachmentId: string) {
      return joinUrl(
        options.baseUrl,
        `/api/v1/store/attachments/${encodeURIComponent(attachmentId)}`
      );
    },

    async fetchAttachmentObjectUrl(attachmentId: string) {
      const res = await fetchImpl(this.attachmentUrl(attachmentId), {
        headers: { Authorization: `Bearer ${options.apiKey}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Maintainer Pro attachment ${attachmentId} failed (${res.status}): ${
            text || res.statusText
          }`
        );
      }
      return URL.createObjectURL(await res.blob());
    },
  };
}

export type MaintainerProClient = ReturnType<typeof createMaintainerProClient>;

export type ChatGoogleUser = {
  name: string;
  email: string;
  type: "client" | "developer";
  role?: string;
};

export function waitForChatGoogleMessage(
  adminOrigin: string,
  popup: Window | null
): Promise<ChatGoogleUser> {
  return new Promise((resolve, reject) => {
    const timer = window.setInterval(() => {
      if (popup && popup.closed) {
        cleanup();
        reject(new Error("Google sign-in was closed"));
      }
    }, 400);
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Google sign-in timed out"));
    }, 5 * 60 * 1000);

    function onMessage(event: MessageEvent) {
      if (event.origin !== adminOrigin) return;
      const data = event.data as {
        type?: string;
        ok?: boolean;
        user?: ChatGoogleUser | null;
        error?: string | null;
      };
      if (data?.type !== "mp-chat-google") return;
      cleanup();
      if (!data.ok || !data.user?.email) {
        reject(new Error(data.error || "Google sign-in failed"));
        return;
      }
      resolve(data.user);
    }

    function cleanup() {
      window.clearInterval(timer);
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
    }

    window.addEventListener("message", onMessage);
  });
}
